import { Fragment, useEffect, useRef, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  AlignCenter, AlignLeft, AlignRight, Bold, ChevronDown, ChevronUp,
  Image, Italic, Minus, Plus, Printer, RotateCcw, Save, Trash2, Type, Upload,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type BlockAlign  = "left" | "center" | "right";
export type BlockSize   = "small" | "normal" | "large" | "xl";

export interface PartStyle {
  bold:   boolean;
  italic: boolean;
  size:   BlockSize;
  align:  BlockAlign;
}

export type ContentSectionId =
  | "store_name" | "order_type"  | "order_number" | "order_date"
  | "ready_time" | "customer"    | "items"         | "totals";

export const DEFAULT_SECTION_ORDER: ContentSectionId[] = [
  "store_name", "order_type", "order_number", "order_date",
  "ready_time", "customer", "items", "totals",
];

export interface ContentSettings {
  sectionOrder:            ContentSectionId[];
  showUnitPrice:           boolean;
  priceBold:               boolean;
  // Per-section text styles
  storeName:    PartStyle;
  orderType:    PartStyle;
  orderNumber:  PartStyle;
  orderDate:    PartStyle;
  readyTime:    PartStyle;
  customerInfo: PartStyle;
  itemName:     PartStyle;
  totals:       PartStyle;
  // Per-section spacing (blank lines after each section)
  spacingAfterStoreName:   number;
  spacingAfterOrderType:   number;
  spacingAfterOrderNumber: number;
  spacingAfterDate:        number;
  spacingAfterReadyTime:   number;
  spacingAfterCustomer:    number;
  spacingBetweenItems:     number;
  spacingAfterItems:       number;
  spacingBeforeFooter:     number;
  // Legacy (kept for backward compat with saved designs)
  showDeliveryTime:        boolean;
  blankLineBetweenItems:   boolean;
  orderInfo:               PartStyle;
  spacingAfterHeader:      number;
  spacingBetweenSections:  number;
}

export type LogoSize = "s" | "m" | "l" | "xl";

export type ReceiptBlock =
  | { id: string; type: "logo";    url: string; align: BlockAlign; logoSize: LogoSize }
  | { id: string; type: "text";    text: string; align: BlockAlign; bold: boolean; italic: boolean; size: BlockSize }
  | { id: string; type: "divider" }
  | { id: string; type: "spacer" };

export interface ReceiptDesign {
  header:  ReceiptBlock[];
  footer:  ReceiptBlock[];
  content: ContentSettings;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const ZERO_PART: PartStyle = { bold: false, italic: false, size: "normal", align: "left" };

export const DEFAULT_CONTENT: ContentSettings = {
  sectionOrder:            DEFAULT_SECTION_ORDER,
  showUnitPrice:           false,
  priceBold:               true,
  storeName:    { bold: true,  italic: false, size: "xl",     align: "center" },
  orderType:    { bold: true,  italic: false, size: "large",  align: "left"   },
  orderNumber:  { bold: true,  italic: false, size: "normal", align: "left"   },
  orderDate:    { bold: false, italic: false, size: "normal", align: "left"   },
  readyTime:    { bold: true,  italic: false, size: "normal", align: "left"   },
  customerInfo: { bold: true,  italic: false, size: "normal", align: "left"   },
  itemName:     { bold: false, italic: false, size: "normal", align: "left"   },
  totals:       { bold: false, italic: false, size: "normal", align: "left"   },
  spacingAfterStoreName:   0,
  spacingAfterOrderType:   0,
  spacingAfterOrderNumber: 0,
  spacingAfterDate:        0,
  spacingAfterReadyTime:   0,
  spacingAfterCustomer:    0,
  spacingBetweenItems:     0,
  spacingAfterItems:       0,
  spacingBeforeFooter:     0,
  showDeliveryTime:        true,
  blankLineBetweenItems:   false,
  orderInfo:               { ...ZERO_PART },
  spacingAfterHeader:      0,
  spacingBetweenSections:  0,
};

const DEFAULT_DESIGN: ReceiptDesign = {
  header: [
    { id: "h1", type: "logo", url: "", align: "center", logoSize: "m" },
    { id: "h2", type: "spacer" },
  ],
  footer: [
    { id: "f1", type: "divider" },
    { id: "f2", type: "text", text: "Thank you for your order!", align: "center", bold: false, italic: false, size: "normal" },
  ],
  content: DEFAULT_CONTENT,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

let _seq = 0;
function uid() { return `b${Date.now()}_${++_seq}`; }

async function uploadFile(file: File): Promise<string> {
  const token = localStorage.getItem("ayrshire_token");
  if (!token) throw new Error("Not authenticated");
  const reqRes = await fetch("/api/storage/uploads/request-url", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
  });
  if (!reqRes.ok) throw new Error("Failed to get upload URL");
  const { uploadURL, objectPath, finalizeToken } = await reqRes.json() as { uploadURL: string; objectPath: string; finalizeToken: string };
  const uploadRes = await fetch(uploadURL, { method: "PUT", body: file, headers: { "Content-Type": file.type, Authorization: `Bearer ${token}` } });
  if (!uploadRes.ok) throw new Error("Upload failed");
  const finalizeRes = await fetch("/api/storage/uploads/finalize", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ finalizeToken }),
  });
  if (!finalizeRes.ok) throw new Error("Failed to finalize upload");
  const path = objectPath.startsWith("/objects") ? objectPath.slice(8) : objectPath;
  return `/api/storage/objects${path}`;
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <div onClick={() => onChange(!on)}
        className={`relative w-9 h-5 rounded-full transition-colors ${on ? "bg-[#1e3a5f]" : "bg-slate-300"}`}
      >
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : ""}`} />
      </div>
      <span className="text-xs text-slate-600">{label}</span>
    </label>
  );
}

function SizeBtn({ label, value, active, onClick }: { label: string; value: BlockSize; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className={`px-2 py-1 text-[11px] font-medium ${active ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
    >{label}</button>
  );
}

// ─── BlockEditor (for header/footer blocks) ───────────────────────────────────

function BlockEditor({ block, onChange, onDelete, onUp, onDown, isFirst, isLast }: {
  block: ReceiptBlock; onChange: (b: ReceiptBlock) => void;
  onDelete: () => void; onUp: () => void; onDown: () => void;
  isFirst: boolean; isLast: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const label =
    block.type === "logo" ? "Logo image" :
    block.type === "text" ? "Text" :
    block.type === "divider" ? "Divider line" : "Spacer";

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || block.type !== "logo") return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      onChange({ ...block, url });
    } catch {
      alert("Upload failed — please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="flex gap-2 items-start rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-col gap-0.5 pt-1">
        <button onClick={onUp} disabled={isFirst} className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-20"><ChevronUp size={14}/></button>
        <div className="w-3.5 h-3.5 text-slate-200 mx-auto flex items-center justify-center">≡</div>
        <button onClick={onDown} disabled={isLast} className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-20"><ChevronDown size={14}/></button>
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        {block.type === "logo" && (
          <div className="space-y-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => void handleFile(e)} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-xs text-slate-600 hover:border-[#1e3a5f] hover:text-primary disabled:opacity-50 w-full"
            ><Upload size={13}/>{uploading ? "Uploading…" : block.url ? "Replace logo image" : "Upload logo image"}</button>
            {block.url && (
              <div className="relative inline-block">
                <img src={block.url} alt="Logo" className="h-14 object-contain rounded border border-slate-100 bg-slate-50 p-1"
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
                <button onClick={() => onChange({ ...block, url: "" })}
                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] hover:bg-red-600">✕</button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {([["S","s"],["M","m"],["L","l"],["XL","xl"]] as [string, LogoSize][]).map(([lbl, sz]) => (
                  <button key={sz} onClick={() => onChange({ ...block, logoSize: sz })}
                    className={`px-2 py-1 text-xs font-medium ${(block.logoSize ?? "m") === sz ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                  >{lbl}</button>
                ))}
              </div>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {(["left","center","right"] as BlockAlign[]).map(a => (
                  <button key={a} onClick={() => onChange({ ...block, align: a })} title={a}
                    className={`px-2 py-1 ${(block.align ?? "center") === a ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                  >
                    {a === "left" ? <AlignLeft size={13}/> : a === "center" ? <AlignCenter size={13}/> : <AlignRight size={13}/>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {block.type === "text" && (
          <div className="space-y-2">
            <textarea value={block.text} onChange={e => onChange({ ...block, text: e.target.value })} rows={2} placeholder="Enter text…"
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
            <div className="flex flex-wrap gap-2">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {(["left","center","right"] as BlockAlign[]).map(a => (
                  <button key={a} onClick={() => onChange({ ...block, align: a })} title={a}
                    className={`px-2 py-1 ${block.align === a ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                  >
                    {a === "left" ? <AlignLeft size={13}/> : a === "center" ? <AlignCenter size={13}/> : <AlignRight size={13}/>}
                  </button>
                ))}
              </div>
              <button onClick={() => onChange({ ...block, bold: !block.bold })}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold ${block.bold ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              ><Bold size={12}/> Bold</button>
              <button onClick={() => onChange({ ...block, italic: !block.italic })}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold ${block.italic ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              ><Italic size={12}/> Italic</button>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                {([["S","small"],["M","normal"],["L","large"],["XL","xl"]] as [string,BlockSize][]).map(([lbl, sz]) => (
                  <SizeBtn key={sz} label={lbl} value={sz} active={block.size === sz} onClick={() => onChange({ ...block, size: sz })} />
                ))}
              </div>
            </div>
          </div>
        )}
        {block.type === "divider" && <div className="border-t border-dashed border-slate-300 my-1" />}
        {block.type === "spacer"  && <div className="text-xs text-slate-400 italic">Empty line</div>}
      </div>
      <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-500 mt-0.5"><Trash2 size={14}/></button>
    </div>
  );
}

// ─── SectionEditor (header/footer block list) ─────────────────────────────────

function SectionEditor({ blocks, onChange }: { blocks: ReceiptBlock[]; onChange: (b: ReceiptBlock[]) => void }) {
  function update(i: number, b: ReceiptBlock) { const n = [...blocks]; n[i] = b; onChange(n); }
  function del(i: number) { onChange(blocks.filter((_, j) => j !== i)); }
  function move(i: number, dir: -1 | 1) {
    const n = [...blocks]; const j = i + dir;
    if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j]!, n[i]!]; onChange(n);
  }
  function add(type: ReceiptBlock["type"]) {
    const base = { id: uid() };
    let b: ReceiptBlock;
    if      (type === "logo")    b = { ...base, type: "logo",    url: "", align: "center" as BlockAlign, logoSize: "m" as LogoSize };
    else if (type === "text")    b = { ...base, type: "text",    text: "", align: "center", bold: false, italic: false, size: "normal" };
    else if (type === "divider") b = { ...base, type: "divider" };
    else                         b = { ...base, type: "spacer" };
    onChange([...blocks, b]);
  }
  return (
    <div className="space-y-3">
      {blocks.length === 0 && <p className="text-xs text-slate-400 italic px-1">No blocks yet — add one below.</p>}
      {blocks.map((b, i) => (
        <BlockEditor key={b.id} block={b} onChange={nb => update(i, nb)}
          onDelete={() => del(i)} onUp={() => move(i, -1)} onDown={() => move(i, 1)}
          isFirst={i === 0} isLast={i === blocks.length - 1} />
      ))}
      <div className="flex flex-wrap gap-2 pt-1">
        <span className="text-xs text-slate-400 self-center">Add:</span>
        <button onClick={() => add("logo")}    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium"><Image   size={12}/> Logo</button>
        <button onClick={() => add("text")}    className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium"><Type    size={12}/> Text</button>
        <button onClick={() => add("divider")} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium"><Minus   size={12}/> Divider</button>
        <button onClick={() => add("spacer")}  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium"><Plus    size={12}/> Spacer</button>
      </div>
    </div>
  );
}

// ─── Content section ordering maps ───────────────────────────────────────────

const SECTION_LABELS: Record<ContentSectionId, string> = {
  store_name:   "Store Name",
  order_type:   "Order Type",
  order_number: "Order Number",
  order_date:   "Date & Time",
  ready_time:   "Ready / Delivery Time",
  customer:     "Customer Info",
  items:        "Items",
  totals:       "Totals & Payment",
};

type StyleKey = keyof Pick<ContentSettings,
  "storeName"|"orderType"|"orderNumber"|"orderDate"|"readyTime"|"customerInfo"|"itemName"|"totals">;
type SpacingKey = keyof Pick<ContentSettings,
  "spacingAfterStoreName"|"spacingAfterOrderType"|"spacingAfterOrderNumber"|"spacingAfterDate"|
  "spacingAfterReadyTime"|"spacingAfterCustomer"|"spacingAfterItems"|"spacingBeforeFooter">;

const SECTION_STYLE: Record<ContentSectionId, StyleKey> = {
  store_name:   "storeName",
  order_type:   "orderType",
  order_number: "orderNumber",
  order_date:   "orderDate",
  ready_time:   "readyTime",
  customer:     "customerInfo",
  items:        "itemName",
  totals:       "totals",
};

const SECTION_SPACING: Record<ContentSectionId, SpacingKey> = {
  store_name:   "spacingAfterStoreName",
  order_type:   "spacingAfterOrderType",
  order_number: "spacingAfterOrderNumber",
  order_date:   "spacingAfterDate",
  ready_time:   "spacingAfterReadyTime",
  customer:     "spacingAfterCustomer",
  items:        "spacingAfterItems",
  totals:       "spacingBeforeFooter",
};

// ─── SectionRow ───────────────────────────────────────────────────────────────

function SectionRow({
  sectionId, style, spacingAfter, spacingBetween,
  onChange, onSpacingAfterChange, onSpacingBetweenChange,
  onUp, onDown, isFirst, isLast,
}: {
  sectionId:   ContentSectionId;
  style:       PartStyle;
  spacingAfter: number;
  spacingBetween?: number;
  onChange:              (s: PartStyle) => void;
  onSpacingAfterChange:  (v: number) => void;
  onSpacingBetweenChange?: (v: number) => void;
  onUp:    () => void;
  onDown:  () => void;
  isFirst: boolean;
  isLast:  boolean;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 px-2.5 py-2 flex-wrap bg-slate-50/60">
        <div className="flex flex-col shrink-0">
          <button onClick={onUp} disabled={isFirst} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 rounded hover:bg-slate-100"><ChevronUp size={13}/></button>
          <button onClick={onDown} disabled={isLast} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20 rounded hover:bg-slate-100"><ChevronDown size={13}/></button>
        </div>
        <span className="text-xs font-semibold text-slate-700 min-w-[118px] shrink-0">{SECTION_LABELS[sectionId]}</span>
        <div className="flex flex-wrap gap-1.5 items-center">
          <button onClick={() => onChange({ ...style, bold: !style.bold })}
            className={`flex items-center gap-0.5 px-1.5 py-1 rounded border text-[11px] font-bold ${style.bold ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          ><Bold size={10}/> B</button>
          <button onClick={() => onChange({ ...style, italic: !style.italic })}
            className={`flex items-center gap-0.5 px-1.5 py-1 rounded border text-[11px] font-semibold italic ${style.italic ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          ><Italic size={10}/> I</button>
          <div className="flex rounded border border-slate-200 overflow-hidden">
            {([["S","small"],["M","normal"],["L","large"],["XL","xl"]] as [string,BlockSize][]).map(([lbl, sz]) => (
              <SizeBtn key={sz} label={lbl} value={sz} active={style.size === sz} onClick={() => onChange({ ...style, size: sz })} />
            ))}
          </div>
          <div className="flex rounded border border-slate-200 overflow-hidden">
            {(["left","center","right"] as BlockAlign[]).map(a => (
              <button key={a} onClick={() => onChange({ ...style, align: a })} title={a}
                className={`px-1.5 py-1 ${style.align === a ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              >
                {a === "left" ? <AlignLeft size={11}/> : a === "center" ? <AlignCenter size={11}/> : <AlignRight size={11}/>}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 px-3 py-1.5 border-t border-slate-100 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-400 shrink-0">Spacing after:</span>
          <div className="flex rounded border border-slate-200 overflow-hidden">
            {[0,1,2,3].map(n => (
              <button key={n} onClick={() => onSpacingAfterChange(n)}
                className={`px-2 py-0.5 ${spacingAfter === n ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-400 hover:bg-slate-50"}`}
              >{n}</button>
            ))}
          </div>
        </div>
        {sectionId === "items" && onSpacingBetweenChange && spacingBetween !== undefined && (
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 shrink-0">Between items:</span>
            <div className="flex rounded border border-slate-200 overflow-hidden">
              {[0,1,2].map(n => (
                <button key={n} onClick={() => onSpacingBetweenChange(n)}
                  className={`px-2 py-0.5 ${spacingBetween === n ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-400 hover:bg-slate-50"}`}
                >{n}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ContentEditor ────────────────────────────────────────────────────────────

function ContentEditor({ content, onChange }: { content: ContentSettings; onChange: (c: ContentSettings) => void }) {
  function set<K extends keyof ContentSettings>(key: K, val: ContentSettings[K]) {
    onChange({ ...content, [key]: val });
  }
  const order: ContentSectionId[] = content.sectionOrder?.length ? content.sectionOrder : DEFAULT_SECTION_ORDER;

  function moveSection(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    set("sectionOrder", next);
  }

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Content Sections</h4>
          <span className="text-[11px] text-slate-400">↑↓ to reorder</span>
        </div>
        <div className="space-y-2">
          {order.map((id, i) => (
            <SectionRow
              key={id}
              sectionId={id}
              style={(content[SECTION_STYLE[id]] ?? DEFAULT_CONTENT[SECTION_STYLE[id]]) as PartStyle}
              spacingAfter={(content[SECTION_SPACING[id]] as number | undefined) ?? 0}
              spacingBetween={id === "items" ? (content.spacingBetweenItems ?? 0) : undefined}
              onChange={v => set(SECTION_STYLE[id], v)}
              onSpacingAfterChange={v => set(SECTION_SPACING[id], v)}
              onSpacingBetweenChange={id === "items" ? v => set("spacingBetweenItems", v) : undefined}
              onUp={() => moveSection(i, -1)}
              onDown={() => moveSection(i, 1)}
              isFirst={i === 0}
              isLast={i === order.length - 1}
            />
          ))}
        </div>
      </div>
      <div className="space-y-2.5">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Options</h4>
        <Toggle on={content.showUnitPrice} onChange={v => set("showUnitPrice", v)} label="Show unit price when qty > 1" />
        <Toggle on={content.priceBold}     onChange={v => set("priceBold", v)}     label="Make item price bold" />
      </div>
    </div>
  );
}

// ─── ThermalPreview ───────────────────────────────────────────────────────────

function sizeStyle(size: BlockSize): string {
  if (size === "xl")    return "text-base leading-tight";
  if (size === "large") return "text-sm";
  if (size === "small") return "text-[10px]";
  return "text-xs";
}
function alignStyle(align: BlockAlign): string {
  if (align === "center") return "text-center";
  if (align === "right")  return "text-right";
  return "text-left";
}
function partClass(s: PartStyle): string {
  return `${sizeStyle(s.size)} ${alignStyle(s.align)} ${s.bold ? "font-bold" : ""} ${s.italic ? "italic" : ""}`.trim();
}

function ThermalPreview({ design }: { design: ReceiptDesign }) {
  const c = design.content;
  const ITEMS = [
    { name: "Chicken Burger", qty: 1, unit: 799,  total: 799  },
    { name: "Large Chips",    qty: 1, unit: 599,  total: 599  },
    { name: "Can of Irn-Bru", qty: 2, unit: 150,  total: 300  },
  ];
  const pence = (p: number) => `£${(p / 100).toFixed(2)}`;
  const subtotal = ITEMS.reduce((s, i) => s + i.total, 0);
  const order: ContentSectionId[] = c.sectionOrder?.length ? c.sectionOrder : DEFAULT_SECTION_ORDER;

  function renderBlock(b: ReceiptBlock, idx: number) {
    if (b.type === "logo") {
      const sz = (b.logoSize ?? "m") === "xl" ? "h-20" : (b.logoSize ?? "m") === "l" ? "h-14" : (b.logoSize ?? "m") === "s" ? "h-6" : "h-10";
      const alignCls = (b.align ?? "center") === "center" ? "mx-auto" : (b.align ?? "center") === "right" ? "ml-auto" : "mr-auto";
      if (!b.url) return <div key={idx} className="h-8 border border-dashed border-slate-200 rounded flex items-center justify-center text-[10px] text-slate-400">[logo image]</div>;
      return <img key={idx} src={b.url} alt="logo" className={`${sz} object-contain ${alignCls} block`} onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />;
    }
    if (b.type === "divider") return <div key={idx} className="border-t border-dashed border-slate-400 my-0.5" />;
    if (b.type === "spacer")  return <div key={idx} className="h-2" />;
    if (b.type === "text") {
      const cls = `${sizeStyle(b.size)} ${alignStyle(b.align)} ${b.bold ? "font-bold" : ""} ${b.italic ? "italic" : ""} w-full block leading-snug`.trim();
      return <p key={idx} className={cls}>{b.text || <span className="text-slate-300 italic">empty text block</span>}</p>;
    }
    return null;
  }

  function Spacer({ n }: { n: number }) {
    return <>{Array.from({ length: n }).map((_, k) => <div key={k} className="h-2" />)}</>;
  }

  const DividerLine = () => <div className="border-t border-dashed border-slate-400 my-0.5" />;

  function getStyle(id: ContentSectionId): PartStyle {
    return (c[SECTION_STYLE[id]] as PartStyle | undefined) ?? DEFAULT_CONTENT[SECTION_STYLE[id]];
  }

  function renderSection(id: ContentSectionId) {
    const s = getStyle(id);
    const pc = `${partClass(s)} w-full block`;
    const sp = (c[SECTION_SPACING[id]] as number | undefined) ?? 0;

    switch (id) {
      case "store_name":
        return <><p className={pc}>Ayrshire Kitchen</p><Spacer n={sp} /></>;

      case "order_type":
        return <><p className={pc}>COLLECTION</p><Spacer n={sp} /></>;

      case "order_number":
        return <><p className={pc}>Order #1042</p><Spacer n={sp} /></>;

      case "order_date":
        return <><p className={pc}>18 Jun 2025&nbsp;&nbsp;14:32</p><Spacer n={sp} /></>;

      case "ready_time":
        return <><p className={pc}>Ready in approx. 20 mins</p><Spacer n={sp} /></>;

      case "customer":
        return <>
          <p className={pc}>Jamie McDonald</p>
          <p className={`${partClass({ ...s, bold: false })} w-full block`}>07712 345 678</p>
          <Spacer n={sp} />
        </>;

      case "items": {
        const itemSpc = c.spacingBetweenItems ?? 0;
        return <>
          <p className="font-bold text-[11px]">ITEMS</p>
          {ITEMS.map((it, i) => (
            <div key={i}>
              <div className="flex justify-between gap-1">
                <span className={`${partClass(s)} flex-1 min-w-0`}>{it.qty}x {it.name}</span>
                <span className={`shrink-0 ${c.priceBold ? "font-bold" : ""}`}>{pence(it.total)}</span>
              </div>
              {c.showUnitPrice && it.qty > 1 && (
                <p className="text-[10px] text-slate-500 pl-3">{pence(it.unit)} each</p>
              )}
              {itemSpc > 0 && i < ITEMS.length - 1 && <div className="h-2" />}
            </div>
          ))}
          <Spacer n={sp} />
        </>;
      }

      case "totals":
        return <>
          <div className={`${partClass(s)} w-full`}>
            <div className="flex justify-between"><span>Subtotal</span><span>{pence(subtotal)}</span></div>
            <div className="flex justify-between font-bold text-sm"><span>TOTAL</span><span>{pence(subtotal)}</span></div>
            <div className="flex justify-between"><span>Payment</span><span>CARD</span></div>
          </div>
          <Spacer n={sp} />
        </>;

      default:
        return null;
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sticky top-4" style={{ fontFamily: "'Courier New', monospace", maxWidth: 280 }}>
      <div className="flex items-center gap-1.5 mb-3">
        <Printer size={13} className="text-slate-400" />
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Thermal Preview</span>
      </div>

      <div className="space-y-0.5 text-[11px]">
        {design.header.map((b, i) => renderBlock(b, i))}
        {order.map((id, idx) => (
          <Fragment key={id}>
            {(idx > 0 || design.header.length > 0) && <DividerLine />}
            {renderSection(id)}
          </Fragment>
        ))}
        {design.footer.length > 0 && <DividerLine />}
        {design.footer.map((b, i) => renderBlock(b, i))}
      </div>

      <div className="pt-3 border-t border-dashed border-slate-200 text-center text-[10px] text-slate-300">— cut —</div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminReceiptDesign() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [design, setDesign] = useState<ReceiptDesign>(DEFAULT_DESIGN);
  const [loading, setLoading]  = useState(true);
  const [saving,  setSaving]   = useState(false);
  const savedRef = useRef<string>("");

  useEffect(() => {
    void (async () => {
      try {
        const token = localStorage.getItem("ayrshire_token");
        const res = await fetch("/api/admin/receipt-design", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json() as { design: string | null };
        if (data.design) {
          const parsed = JSON.parse(data.design) as Partial<ReceiptDesign>;
          const saved = (parsed.content ?? {}) as Partial<ContentSettings>;
          setDesign({
            header:  parsed.header  ?? DEFAULT_DESIGN.header,
            footer:  parsed.footer  ?? DEFAULT_DESIGN.footer,
            content: {
              ...DEFAULT_CONTENT,
              ...saved,
              // Deep-merge nested PartStyle objects so new fields are always populated
              storeName:    { ...DEFAULT_CONTENT.storeName,    ...(saved.storeName    ?? {}) },
              orderType:    { ...DEFAULT_CONTENT.orderType,    ...(saved.orderType    ?? {}) },
              orderNumber:  { ...DEFAULT_CONTENT.orderNumber,  ...(saved.orderNumber  ?? {}) },
              orderDate:    { ...DEFAULT_CONTENT.orderDate,    ...(saved.orderDate    ?? {}) },
              readyTime:    { ...DEFAULT_CONTENT.readyTime,    ...(saved.readyTime    ?? {}) },
              customerInfo: { ...DEFAULT_CONTENT.customerInfo, ...(saved.customerInfo ?? {}) },
              itemName:     { ...DEFAULT_CONTENT.itemName,     ...(saved.itemName     ?? {}) },
              totals:       { ...DEFAULT_CONTENT.totals,       ...(saved.totals       ?? {}) },
              orderInfo:    { ...DEFAULT_CONTENT.orderInfo,    ...(saved.orderInfo    ?? {}) },
              sectionOrder: saved.sectionOrder ?? DEFAULT_SECTION_ORDER,
            },
          });
          savedRef.current = data.design;
        }
      } catch { /* use defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  async function save() {
    setSaving(true);
    try {
      const token = localStorage.getItem("ayrshire_token");
      const designStr = JSON.stringify(design);
      const res = await fetch("/api/admin/receipt-design", {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ design: designStr }),
      });
      if (!res.ok) throw new Error("Save failed");
      savedRef.current = designStr;
      toast({ title: "Receipt design saved", description: "Applied globally to all stores." });
    } catch {
      toast({ title: "Error", description: "Could not save receipt design.", variant: "destructive" });
    } finally { setSaving(false); }
  }

  function reset() {
    if (!confirm("Reset to default design?")) return;
    setDesign(DEFAULT_DESIGN);
  }

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;
  const isDirty = JSON.stringify(design) !== savedRef.current;

  return (
    <AdminLayout current="/admin/receipt-design">
      <div className="space-y-6 max-w-5xl">

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Receipt Designer</h1>
            <p className="text-sm text-slate-500 mt-1">
              Design the global receipt template — applied to all stores on every printer type.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={reset}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
            ><RotateCcw size={14}/> Reset</button>
            <button onClick={() => void save()} disabled={saving || !isDirty}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-semibold hover:bg-[#162d4a] disabled:opacity-50"
            ><Save size={14}/> {saving ? "Saving…" : "Save Design"}</button>
          </div>
        </div>

        {isDirty && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg text-xs text-amber-700 font-medium">
            Unsaved changes
          </div>
        )}

        {loading ? (
          <div className="text-slate-400 text-sm py-12 text-center">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 items-start">

            <div className="space-y-6">

              <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#1e3a5f]" />
                  <h2 className="font-bold text-foreground">Header</h2>
                  <span className="text-xs text-slate-400">— printed above the order</span>
                </div>
                <SectionEditor blocks={design.header} onChange={blocks => setDesign(d => ({ ...d, header: blocks }))} />
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#059669]" />
                  <h2 className="font-bold text-foreground">Order Content</h2>
                  <span className="text-xs text-slate-400">— reorder sections, set size, alignment & spacing</span>
                </div>
                <ContentEditor content={design.content} onChange={content => setDesign(d => ({ ...d, content }))} />
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#f0a500]" />
                  <h2 className="font-bold text-foreground">Footer</h2>
                  <span className="text-xs text-slate-400">— printed below totals</span>
                </div>
                <SectionEditor blocks={design.footer} onChange={blocks => setDesign(d => ({ ...d, footer: blocks }))} />
              </div>

              <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 rounded-xl">
                <span className="text-blue-500 mt-0.5">🈷️</span>
                <p className="text-xs text-blue-700">
                  <span className="font-semibold">Mandarin translation</span> is configured per store and printed automatically when enabled. Italic formatting is shown in the preview but may not render on all printer models.
                </p>
              </div>
            </div>

            <ThermalPreview design={design} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
